import { morph } from '@theme/morph';
import { Component } from '@theme/component';
import { StandardEvents } from '@shopify/events';

class ProductInventory extends Component {
  connectedCallback() {
    super.connectedCallback();

    const closestSection = this.closest('.shopify-section, dialog');

    closestSection?.addEventListener(
      StandardEvents.productSelect,
      this.#handleProductSelect
    );

    this.#updateInitialStatus();
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    const closestSection = this.closest('.shopify-section, dialog');

    closestSection?.removeEventListener(
      StandardEvents.productSelect,
      this.#handleProductSelect
    );
  }

  #updateInitialStatus() {
    const quantity = Number(
      this.dataset.inventoryQuantity
    );

    this.#updateInventoryState(quantity);
  }

  #updateInventoryState(quantity) {
    const content = this.querySelector(
      '[data-inventory-content]'
    );

    if (!content) return;

    const isReadyToShip =
      Number.isFinite(quantity) && quantity > 0;

    const status = isReadyToShip
      ? 'ready-to-ship'
      : 'made-to-order';

    content.classList.remove(
      'product-inventory__content--ready-to-ship',
      'product-inventory__content--made-to-order'
    );

    content.classList.add(
      `product-inventory__content--${status}`
    );

    this.dataset.inventoryQuantity = String(quantity);

    /*
     * IMPORTANT:
     * Do NOT change heading or description text here.
     *
     * The text comes from product-inventory.liquid
     * and therefore from the Shopify Theme Editor.
     */

    const quantityElement = this.querySelector(
      '[data-inventory-quantity-text]'
    );

    const showQuantity =
      this.dataset.showQuantity === 'true';

    if (
      quantityElement &&
      showQuantity &&
      isReadyToShip &&
      quantity > 0
    ) {
      quantityElement.textContent =
        `${quantity} available`;

      quantityElement.hidden = false;
    } else if (quantityElement) {
      quantityElement.textContent = '';
      quantityElement.hidden = true;
    }
  }

  #handleProductSelect = (event) => {
    if (
      !(event.target instanceof Element) ||
      event.target.closest('product-card')
    ) {
      return;
    }

    event.promise
      .then(({ detail }) => {
        if (!detail?.html) return;

        const {
          html,
          newProduct,
          resource,
        } = detail;

        if (newProduct) {
          this.dataset.productId = newProduct.id;
        } else if (
          detail.productId &&
          detail.productId !== this.dataset.productId
        ) {
          return;
        }

        /*
         * Shopify provides the selected variant
         * as `resource`.
         */
        if (
          resource &&
          typeof resource === 'object'
        ) {
          const quantity = Number(
            resource.inventory_quantity
          );

          if (Number.isFinite(quantity)) {
            this.#updateInventoryState(quantity);
          }
        }

        /*
         * Get the newly rendered inventory block.
         *
         * This keeps the Liquid-generated heading,
         * description, icon and settings intact.
         */
        const newInventory =
          html.querySelector('product-inventory');

        if (!newInventory) return;

        /*
         * Copy the rendered content from Shopify.
         *
         * This is important because the heading,
         * description and icon are generated by Liquid
         * from the Theme Editor settings.
         */
        const newContent =
          newInventory.querySelector(
            '[data-inventory-content]'
          );

        if (newContent) {
          const currentContent =
            this.querySelector(
              '[data-inventory-content]'
            );

          if (currentContent) {
            morph(
              currentContent,
              newContent
            );
          }
        }

        /*
         * Update inventory quantity after morphing.
         */
        const newQuantity = Number(
          newInventory.dataset.inventoryQuantity
        );

        if (Number.isFinite(newQuantity)) {
          this.dataset.inventoryQuantity =
            String(newQuantity);

          this.#updateInventoryState(
            newQuantity
          );
        }
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') {
          console.warn(
            '[product-inventory] Event promise rejected:',
            error
          );
        }
      });
  };
}

if (
  !customElements.get('product-inventory')
) {
  customElements.define(
    'product-inventory',
    ProductInventory
  );
}